/**
 * QuoteReview.jsx
 * ----------------------------------------------------------------------
 * PUBLIC page — no login. Customer taps the SMS link `/q/{token}`,
 * lands here, sees the invoice PDF inline + Approve / Decline buttons.
 *
 * Backed by /api/public/quotes/{token} — no cookies needed.
 *
 * Security note: the token lives in the URL so anyone with the link can
 * view + decide. Treat it like a magic-link login — the SM should NEVER
 * paste the link into a public channel.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  CheckCircle2, XCircle, FileText, Phone, MapPin, Loader2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (_e) { return ''; }
}

export default function QuoteReview() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineNote, setDeclineNote] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/quotes/${token}`);
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'This quote link is invalid or has expired.');
      }
      const json = await r.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load the quote.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const decide = async (decision, note = null) => {
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/quotes/${token}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note }),
      });
      if (r.ok) {
        toast.success(decision === 'approved' ? 'Quote approved — thank you!' : 'Quote declined.');
        await fetchData();
        setShowDecline(false);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not record your decision.');
      }
    } catch (_e) {
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Quote unavailable</h1>
          <p className="text-slate-600 mb-4">{error}</p>
          <p className="text-sm text-slate-500">
            Please contact the workshop directly for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const inv = data.invoice || {};
  const shop = data.shop || {};
  const decided = !!inv.decision;
  const decisionBadge = inv.decision === 'approved'
    ? { Icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-800 border-emerald-200', text: `Approved on ${fmtDate(inv.decision_at)}` }
    : inv.decision === 'declined'
      ? { Icon: XCircle, color: 'bg-rose-50 text-rose-800 border-rose-200', text: `Declined on ${fmtDate(inv.decision_at)}` }
      : null;

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Top branded header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-slate-900">{shop.name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
            {shop.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {shop.phone}</span>}
            {shop.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {shop.address}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 mt-6">
        {/* Quote summary card */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Quote {inv.doc_no}
              </h2>
              <p className="text-sm text-slate-500">
                For your {inv.vehicle_make} {inv.vehicle_model} ({inv.vehicle_rego || 'no rego'})
              </p>
            </div>
            {decisionBadge && (
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${decisionBadge.color} flex items-center gap-1`} data-testid="decision-badge">
                <decisionBadge.Icon className="h-4 w-4" />
                {decisionBadge.text}
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-500">Subtotal</p>
              <p className="font-semibold text-slate-900">{fmtMoney(inv.subtotal)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">GST</p>
              <p className="font-semibold text-slate-900">{fmtMoney(inv.tax_total)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-lg font-bold text-blue-700">{fmtMoney(inv.total)}</p>
            </div>
          </div>

          {/* Notes / scope */}
          {inv.notes && (
            <div className="mt-4 p-3 rounded-md bg-slate-50 text-sm text-slate-700 prose prose-sm max-w-none"
                  
                 dangerouslySetInnerHTML={{ __html: inv.notes }} />
          )}
        </section>

        {/* PDF preview */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 mt-4" data-testid="pdf-section">
          <div className="flex items-center justify-between mb-2 px-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Full quote PDF
            </h3>
            <a
              href={`${BACKEND_URL}/api/public/quotes/${token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-pdf"
              className="text-xs text-blue-600 hover:underline"
            >
              Download
            </a>
          </div>
          <iframe
            src={`${BACKEND_URL}/api/public/quotes/${token}/pdf`}
            title="Quote PDF"
            className="w-full rounded-lg border border-slate-200"
            style={{ height: '70vh' }}
          />
        </section>

        {/* Action buttons */}
        {!decided && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mt-4 sticky bottom-2" data-testid="decision-row">
            <p className="text-sm text-slate-600 mb-4">
              Happy with the quote? Tap <strong>Approve</strong> and we'll start the work. If you'd like changes, tap <strong>Decline</strong> and tell us what to adjust.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => decide('approved')}
                disabled={submitting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold"
                data-testid="approve-btn"
              >
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Approve quote
              </Button>
              <Button
                onClick={() => setShowDecline(true)}
                disabled={submitting}
                variant="outline"
                className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50 h-12 text-base"
                data-testid="decline-btn"
              >
                <XCircle className="h-5 w-5 mr-2" />
                Decline
              </Button>
            </div>
          </section>
        )}

        {showDecline && !decided && (
          <section className="bg-white rounded-2xl shadow-sm border border-rose-200 p-6 mt-4">
            <h3 className="text-base font-semibold text-rose-800 mb-2">Tell us what to change</h3>
            <Textarea
              value={declineNote}
              onChange={(e) => setDeclineNote(e.target.value.slice(0, 500))}
              placeholder="e.g. Please remove the wiper replacement — I'll do that myself."
              rows={4}
              className="mb-3"
              data-testid="decline-note"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => setShowDecline(false)}
                variant="outline"
                data-testid="decline-cancel"
              >
                Back
              </Button>
              <Button
                onClick={() => decide('declined', declineNote)}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={submitting || !declineNote.trim()}
                data-testid="decline-submit"
              >
                Send decline
              </Button>
            </div>
          </section>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          This quote expires {fmtDate(inv.expires_at)}.
        </p>
      </main>
    </div>
  );
}
